import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import TwLoadEmptyOrAddTag from './TwLoadEmptyOrAddTag';
import './TwLoadTags.scss';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TwEditDeleteTag from './TwEditDeleteTag';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import TwDeleteTagModal from './TwDeleteTagModal';
import { ISearchTagsResponse, TagsPalette } from '../../redux/api/loadTags/loadTags.model';
import { useDeleteTagMutation, useRemoveTagMutation } from '../../redux/api/loadTags/loadTagsApi';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import TwTooltip from '../TwTooltip/TwTooltip';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import { SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import useSize from '../../hooks/useSize';
import {
  setLoadDetailReload,
  setTripDetailReload
} from '../../redux/features/navigation/navigationSlice';
import { EntityType } from '../../redux/Global.model';
import { useDeleteTagFromTripMutation } from '../../redux/api/trips/tripApi';

interface TwLoadTagsProps {
  loadId: string;
  tripId?: string;
  entity: EntityType;
  tags: ISearchTagsResponse[];
  containerSx?: SxProps;
  isCalendar?: boolean;
  readOnly?: boolean;
  wrapTags?: boolean;
  reloadData?: () => void;
}

const TwLoadTags: FC<TwLoadTagsProps> = ({
  loadId,
  tripId,
  entity,
  tags,
  containerSx,
  readOnly,
  wrapTags = true,
  reloadData
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { data } = useAuth();
  const isDriver = data.userData?.role === USER_ROLE.DRIVER;
  const isAdminOrOwner =
    data.userData?.role === USER_ROLE.ADMIN || data.userData?.role === USER_ROLE.OWNER;

  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [tagRefToEdit, setTagRefToEdit] = useState<HTMLDivElement | null>(null);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<ISearchTagsResponse | null>(null);
  const [visibleTags, setVisibleTags] = useState<ISearchTagsResponse[]>([]);
  const [remainingTags, setRemainingTags] = useState<ISearchTagsResponse[]>([]);
  const tagRef = useRef<any>([]);
  const tagsContainerRef = useRef<any>(null);
  const counterRef = useRef<any>(null);
  const [removeTagFromOrder] = useRemoveTagMutation();
  const [removeTagFromTrip] = useDeleteTagFromTripMutation();
  const [deleteTag] = useDeleteTagMutation();
  const { addNotification } = useNotifications();
  const tripsFilterParams = useAppSelector((state) => state.activeTrips.filters);
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const orderDetailOpen = useAppSelector((state) => state.navigation.isLoadDetailOpen);

  const { width: tagsContainerWidth } = useSize(tagsContainerRef);

  const handleEditClick = (refIndex: number, tag: ISearchTagsResponse): void => {
    if (openEdit) {
      handleEditClose();
    } else {
      setTagRefToEdit(tagRef.current[refIndex]);
      setOpenEdit(true);
      setSelectedTag(tag);
    }
  };

  const handleClickDelete = (): void => {
    if (isAdminOrOwner) {
      setOpenDelete(true);
      setOpenEdit(false);
    } else {
      addNotification({
        message: 'Unauthorized to delete tags',
        variant: 'error'
      });
    }
  };

  const handleEditClose = (): void => {
    setOpenEdit(false);
    setTagRefToEdit(null);
    setSelectedTag(null);
  };

  const handleRemoveTag = (tagId: string): void => {
    if (!tagId) {
      addNotification({
        message: 'Error: no tag found',
        variant: 'error'
      });

      return;
    }
    if (!loadId && entity === EntityType.ORDER) {
      addNotification({
        message: 'Error removing tag from order, please try again',
        variant: 'error'
      });

      return;
    }

    if (!tripId && entity === EntityType.TRIP) {
      addNotification({
        message: 'Error removing tag from trip, please try again',
        variant: 'error'
      });

      return;
    }

    if (entity === EntityType.ORDER) {
      removeTagFromOrder({
        loadId: loadId,
        tagId: tagId,
        searchTripParams: {
          filters: {
            ...tripsFilterParams,
            includeArchived: matchesMobileOrTablet
          }
        }
      })
        .unwrap()
        .then((response) => {
          addNotification({
            message: `Tag successfully removed from ${entity.toLowerCase()}`,
            variant: 'success'
          });
          if (orderDetailOpen) {
            dispatch(setLoadDetailReload(true));
          } else {
            dispatch(setTripDetailReload(true));
          }
        })
        .catch((error) => {
          addNotification({
            message: error.message || 'Error removing tag from order',
            variant: 'error'
          });
        });
    } else {
      removeTagFromTrip({
        tripId: tripId || '',
        tagId: tagId,
        searchTripParams: {
          filters: {
            ...tripsFilterParams,
            includeArchived: matchesMobileOrTablet
          }
        }
      })
        .unwrap()
        .then((response) => {
          addNotification({
            message: `Tag successfully removed from ${entity.toLowerCase()}`,
            variant: 'success'
          });

          dispatch(setTripDetailReload(true));
        })
        .catch((error) => {
          addNotification({
            message: error.message || 'Error removing tag from trip',
            variant: 'error'
          });
        });
    }
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDelete(false);
  };

  const handleDeleteTag = (): void => {
    if (!selectedTag) return;
    if (!isAdminOrOwner) return;

    deleteTag(selectedTag.id)
      .unwrap()
      .then((res) => {
        addNotification({
          message: 'Tag deleted successfully',
          variant: 'success'
        });
        handleCloseDeleteModal();
        setSelectedTag(null);
        handleEditClose();
      })
      .catch((err) => {
        addNotification({
          message: 'Error deleting tag, please try again',
          variant: 'error'
        });
      });
  };

  useLayoutEffect(() => {
    if (tagsContainerRef.current) {
      // calculate how many tags can fit on the container
      const containerWidth = tagsContainerWidth;

      // fixed width of icons and counter
      const addIcon = !isDriver ? 24 : 0; // add icon width
      const editIcon = 8; // edit icon width when hover on tag
      const counter = counterRef.current ? counterRef.current.clientWidth : 0; // counter width and gap
      const tagPadding = 16; // padding in tag (left and right)

      // initial width of tags considering icons and counter
      let tagsWidth = addIcon + editIcon + counter + tagPadding;

      const visTags: ISearchTagsResponse[] = [];

      const sortedTags = tags ? [...tags] : [];
      sortedTags.sort((a: ISearchTagsResponse, b: ISearchTagsResponse) =>
        a.name.localeCompare(b.name)
      );

      if (tags && tags.length > 0) {
        sortedTags.forEach((tag, i) => {
          const tagWidth = tag.name.length * 12; // tag width based on the length of content and font size is 12px
          const tagGap = i === 0 ? 0 : 8; // gap between tags

          wrapTags ? (tagsWidth = tagsWidth / 2) : null;

          tagsWidth += tagWidth + tagGap;
          if (containerWidth > tagsWidth) {
            visTags.push(tag);
          }
        });
      }

      const visibleTags = sortedTags.slice(0, visTags.length);
      const remainingTags = sortedTags.slice(visTags.length, sortedTags.length);

      setVisibleTags(visibleTags);
      setRemainingTags(remainingTags);
    }
  }, [JSON.stringify(tags), tagsContainerWidth, counterRef.current, wrapTags]);

  const remainingTagsTooltipText = useMemo(() => {
    let text = '';

    remainingTags.forEach((t, i) => {
      text = text + t.name;
      if (i < remainingTags.length - 1) {
        text = text + ', ';
      }
    });

    return text;
  }, [remainingTags.length]);

  return (
    <>
      <Stack
        flexDirection={'row'}
        flexWrap={wrapTags ? 'wrap' : 'nowrap'}
        gap={1}
        className="load-tags__container"
        ref={tagsContainerRef}
        sx={containerSx}
        onClick={(e): void => {
          e.stopPropagation();
        }}
      >
        {visibleTags.length > 0 &&
          visibleTags.map((tag, i) => (
            <Chip
              ref={(el): any => (tagRef.current[i] = el)}
              className="load-tags__option__tag load-tags__tag-edit"
              variant="outlined"
              key={tag.id}
              label={tag.name}
              sx={{
                backgroundColor: TagsPalette[tag.color].background,
                color: TagsPalette[tag.color].text,
                borderColor: TagsPalette[tag.color].border
              }}
              icon={
                !isDriver && !readOnly ? (
                  <Box
                    className="load-tags__edit-menu"
                    sx={{
                      backgroundColor: TagsPalette[tag.color].background
                    }}
                    onClick={(): void => handleEditClick(i, tag)}
                  >
                    <TwTooltip title="Edit">
                      <MoreHorizIcon className="load-tags__edit-menu__edit-icon" />
                    </TwTooltip>
                  </Box>
                ) : (
                  <></>
                )
              }
              onDelete={(): void => {
                !isDriver ? handleRemoveTag(tag.id) : null;
              }}
              deleteIcon={
                !isDriver && !readOnly ? (
                  <CloseIcon className="load-tags__edit-menu__edit-icon" />
                ) : (
                  <></>
                )
              }
            />
          ))}
        {remainingTags.length > 0 && (
          <TwTooltip title={remainingTagsTooltipText}>
            <Typography
              ref={counterRef}
              component={'span'}
              sx={{
                fontSize: '14px',
                color: '#6B7280'
              }}
            >
              {' '}
              +{remainingTags.length}
            </Typography>
          </TwTooltip>
        )}
        {!isDriver && !readOnly && (
          <TwLoadEmptyOrAddTag
            entity={entity}
            tagsExist={tags && tags.length > 0}
            loadId={loadId}
            tripId={tripId}
            editTagOpen={openEdit}
            onAddTagOpen={handleEditClose}
            reloadData={reloadData}
          />
        )}
      </Stack>

      <TwEditDeleteTag
        anchor={tagRefToEdit}
        open={openEdit}
        handleClickDelete={handleClickDelete}
        tagId={selectedTag?.id || ''}
        handleClose={handleEditClose}
        orderId={loadId}
        reloadData={reloadData}
      />

      <TwDeleteTagModal
        openDeleteConfirmation={openDelete}
        closeDeleteModal={handleCloseDeleteModal}
        tagToDelete={selectedTag?.name || ''}
        onDelete={handleDeleteTag}
      />
    </>
  );
};

export default TwLoadTags;
